import { useState } from 'react';
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconPencil,
  IconTrash,
  IconUser,
  IconMail,
  IconPhone,
  IconMapPin,
  IconStar,
} from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ClientsService, type Client } from '@/services/clients';
import { documentTypeLabel } from '@/lib/document-types';
import { NouClientModal } from '@/components/clients/NouClientModal';
import { EditClientModal } from '@/components/clients/EditClientModal';
import { CreateCompanyModal } from '@/components/clients/CreateCompanyModal';
import { IconBuilding } from '@tabler/icons-react';
import { useClients } from '@/hooks/useClients';
import { ClientDetailSheet } from '@/components/clients/ClientDetailSheet';

export default function ClientsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActiu, setFilterActiu] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showNouModal, setShowNouModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [detailClient, setDetailClient] = useState<Client | null>(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const { success, error } = useToast();

  const {
    data: clientsResponse,
    isLoading: loading,
    refetch: refetchClients,
  } = useClients({
    page: currentPage,
    searchTerm,
    actiuFilter: filterActiu,
    perPage: 15,
  });

  const clients = clientsResponse?.data || [];
  const totalPages = clientsResponse?.meta.total_pages || 1;
  const totalItems = clientsResponse?.meta.total || 0;

  const eliminarClient = async (id: string) => {
    if (!window.confirm('Estàs segur que vols eliminar aquest client?')) {
      return;
    }

    try {
      await ClientsService.delete(id);
      success('Client eliminat correctament');
      refetchClients();
    } catch (err: any) {
      error(err.message || 'Error en eliminar el client');
      console.error(err);
    }
  };

  const getEstatBadge = (actiu: boolean) => {
    if (actiu) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          Actiu
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-gray-100 text-gray-800">
        Inactiu
      </Badge>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ca-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getVipIcon = (client: Client) => {
    if (client.es_vip || client.total_reserves >= 5 || client.total_gastat >= 2000) {
      return <IconStar className="h-4 w-4 text-yellow-500" title="Client VIP" />;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 bg-gradient-to-br from-green-200 to-blue-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-tr from-blue-200 to-indigo-200 rounded-full blur-3xl opacity-15 animate-pulse delay-300"></div>
      </div>

      <div className="relative flex flex-col gap-8 py-8 px-6 md:px-8">
        {/* Header */}
        <div className="animate-in fade-in-2 slide-in-from-top-4 duration-1000">
          <div className="flex items-center justify-between bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Clients</h1>
              <p className="text-lg text-gray-600">
                <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  Gestiona els teus clients
                </span>
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowNouModal(true)}
                className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl h-12 px-6"
              >
                <IconPlus className="mr-2 h-5 w-5" />
                Nou Client
              </Button>
              <Button
                onClick={() => setShowCompanyModal(true)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl h-12 px-6"
              >
                <IconBuilding className="mr-2 h-5 w-5" />
                Nova Empresa
              </Button>
            </div>
          </div>
        </div>

        {/* Filtres i cerca */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-200">
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 items-center space-x-4">
                <div className="relative flex-1 max-w-sm group">
                  <IconSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 transition-colors duration-300 group-focus-within:text-green-600" />
                  <Input
                    placeholder="Cercar per nom, email, DNI o telèfon..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl h-12 transition-all duration-300"
                  />
                </div>
                <Select value={filterActiu} onValueChange={setFilterActiu}>
                  <SelectTrigger className="w-40 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl h-12 transition-all duration-300">
                    <SelectValue placeholder="Estat" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-gray-200 rounded-xl shadow-xl">
                    <SelectItem
                      value="all"
                      className="rounded-lg hover:bg-green-50 transition-colors duration-200"
                    >
                      Tots
                    </SelectItem>
                    <SelectItem
                      value="actiu"
                      className="rounded-lg hover:bg-green-50 transition-colors duration-200"
                    >
                      Actius
                    </SelectItem>
                    <SelectItem
                      value="inactiu"
                      className="rounded-lg hover:bg-green-50 transition-colors duration-200"
                    >
                      Inactius
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Estadístiques */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-400">
          <div className="grid gap-6 md:grid-cols-4">
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                    {totalItems}
                  </div>
                  <p className="text-gray-600 font-medium">Total Clients</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-blue-600 p-3 rounded-2xl">
                  <IconUser className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-green-600">
                    {clients.filter((c) => c.actiu).length}
                  </div>
                  <p className="text-gray-600 font-medium">Clients Actius</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 rounded-2xl">
                  <IconUser className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-yellow-600">
                    {
                      clients.filter(
                        (c) => c.es_vip || c.total_reserves >= 5 || c.total_gastat >= 2000,
                      ).length
                    }
                  </div>
                  <p className="text-gray-600 font-medium">Clients VIP</p>
                </div>
                <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-3 rounded-2xl">
                  <IconStar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-blue-600">
                    {clients.length > 0
                      ? formatCurrency(
                          clients.reduce(
                            (sum, c) => sum + parseFloat(String(c.total_gastat || 0)),
                            0,
                          ) / clients.length,
                        )
                      : formatCurrency(0)}
                  </div>
                  <p className="text-gray-600 font-medium">Gastat Mitjà</p>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-2xl">
                  <IconMail className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Taula */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-600">
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-green-50/50">
                  <TableHead className="font-semibold text-gray-700">Client</TableHead>
                  <TableHead className="font-semibold text-gray-700">Contacte</TableHead>
                  <TableHead className="font-semibold text-gray-700">Ubicació</TableHead>
                  <TableHead className="font-semibold text-gray-700">Estadístiques</TableHead>
                  <TableHead className="font-semibold text-gray-700">Estat</TableHead>
                  <TableHead className="text-right font-semibold text-gray-700">Accions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        <span className="text-gray-600">Carregant clients...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="text-gray-500">No s'han trobat clients</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow
                      key={client.id}
                      className="border-gray-200 hover:bg-green-50/30 transition-all duration-200"
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-3">
                          <div className="bg-gradient-to-r from-green-500 to-blue-600 p-2 rounded-xl">
                            <IconUser className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">
                                {client.nom_complet}
                              </span>
                              {getVipIcon(client)}
                            </div>
                            {client.num_document && (
                              <div className="text-sm text-gray-500">
                                {documentTypeLabel(client.tipus_document)}: {client.num_document}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <IconMail className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-gray-700">{client.email}</span>
                          </div>
                          {client.telefon && (
                            <div className="flex items-center space-x-2">
                              <IconPhone className="h-4 w-4 text-blue-600" />
                              <span className="text-sm text-gray-700">{client.telefon}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <IconMapPin className="h-4 w-4 text-blue-600" />
                          <div className="text-sm">
                            <div className="text-gray-700">{client.ciutat || '-'}</div>
                            <div className="text-gray-500">{client.pais || '-'}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="text-gray-700">{client.total_reserves} reserves</div>
                          <div className="font-semibold text-green-600">
                            {formatCurrency(client.total_gastat)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getEstatBadge(client.actiu)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0 hover:bg-green-100 rounded-xl transition-all duration-200"
                            >
                              <span className="sr-only">Obrir menú</span>
                              <IconPencil className="h-4 w-4 text-gray-600" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-white/95 backdrop-blur-xl border-gray-200 rounded-xl shadow-xl"
                          >
                            <DropdownMenuLabel className="text-gray-700 font-semibold">
                              Accions
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                              className="rounded-lg hover:bg-green-50 transition-colors duration-200"
                              onClick={() => {
                                setDetailClient(client);
                                setShowDetailSheet(true);
                              }}
                            >
                              <IconEye className="mr-2 h-4 w-4 text-blue-600" />
                              Veure detalls
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="rounded-lg hover:bg-green-50 transition-colors duration-200"
                              onClick={() => {
                                setSelectedClient(client);
                                setShowEditModal(true);
                              }}
                            >
                              <IconPencil className="mr-2 h-4 w-4 text-green-600" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-gray-200" />
                            <DropdownMenuItem
                              className="text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-200"
                              onClick={() => eliminarClient(client.id)}
                            >
                              <IconTrash className="mr-2 h-4 w-4" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Paginació */}
        {totalPages > 1 && (
          <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-800">
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl">
              <div className="flex items-center justify-between">
                <p className="text-gray-600">
                  Mostrant {(currentPage - 1) * 15 + 1} a {Math.min(currentPage * 15, totalItems)}{' '}
                  de {totalItems} clients
                </p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="border-gray-200 hover:bg-green-50 hover:border-green-300 rounded-xl px-4 py-2 transition-all duration-300 disabled:opacity-50"
                  >
                    Anterior
                  </Button>
                  <span className="text-gray-700 font-medium px-3 py-2 bg-green-50 rounded-xl border border-green-200">
                    Pàgina {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="border-gray-200 hover:bg-green-50 hover:border-green-300 rounded-xl px-4 py-2 transition-all duration-300 disabled:opacity-50"
                  >
                    Següent
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal per afegir nou client */}
        <NouClientModal
          open={showNouModal}
          onOpenChange={setShowNouModal}
          onSuccess={() => refetchClients()}
        />

        {/* Modal per editar client */}
        {selectedClient && (
          <EditClientModal
            open={showEditModal}
            onOpenChange={setShowEditModal}
            onSuccess={() => refetchClients()}
            client={selectedClient}
          />
        )}

        {/* Modal per crear empresa */}
        <CreateCompanyModal
          open={showCompanyModal}
          onOpenChange={setShowCompanyModal}
          onSuccess={() => refetchClients()}
        />

        {/* Detail sheet per veure perfil client */}
        <ClientDetailSheet
          client={detailClient}
          open={showDetailSheet}
          onClose={() => {
            setShowDetailSheet(false);
            setDetailClient(null);
          }}
        />
      </div>
    </div>
  );
}
